import React, { useEffect, useState } from "react";
import {
  Table,
  Spinner,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import axiosInstance from "../../Request"; 
import { URL } from "../../api"; 
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const LoanCardReporting = () => {

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("customer_id"); 
  const [filterValue, setFilterValue] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

 
  const fetchLoanCardReport = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const url = `${URL.LOAN_CARD_REPORT}${params ? `?${params}` : ""}`;
      const { data } = await axiosInstance.get(url);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching Loan Card data:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load: all rows
  useEffect(() => {
    fetchLoanCardReport({});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const filters = {};
    if (filterValue.trim()) filters[filterType] = filterValue.trim();
    if (fromDate) filters.from_date = fromDate;
    if (toDate) filters.to_date = toDate;
    fetchLoanCardReport(filters);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Loan_Card_Report");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      "Loan_Card_Report.xlsx"
    );
  };

  // Number formatter helper
  const fmt = (v) =>
    v === null || v === undefined || v === "" ? "" : Number(v).toLocaleString();

  // Small font styles reused across table & controls
  const font12 = { fontSize: "10px" };
  const inputSmStyle = { ...font12, height: "30px", padding: "2px 6px" };
  const buttonSmStyle = {
    ...font12,
    padding: "4px 10px",
    height: "30px",
    lineHeight: "1",
  };

  const currentLabel = filterType === "customer_id" ? "Customer ID" : "Loan ID";

  return (
    <div style={font12}>

      {/* Filter Form */}
      <Form onSubmit={handleSearch} className="mb-3">
        <div
          className="d-flex align-items-end flex-wrap"
          style={{
            gap: "10px",
            overflowX: "auto",
            paddingBottom: "4px",
            whiteSpace: "nowrap",
          }}
        >
          {/* Filter Type */}
          <FormGroup className="mb-0" style={{ minWidth: "130px", ...font12 }}>
            <Label for="filterType" className="mb-1 fw-bold" style={font12}>
              Filter Type
            </Label>
            <Input
              bsSize="sm"
              type="select"
              id="filterType"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={inputSmStyle}
            >
              <option value="customer_id">Customer ID</option>
              <option value="loan_id">Loan ID</option>
            </Input>
          </FormGroup>

          {/* Filter Value */}
          <FormGroup className="mb-0" style={{ minWidth: "160px", ...font12 }}>
            <Label for="filterValue" className="mb-1 fw-bold" style={font12}>
              {currentLabel}
            </Label>
            <Input
              bsSize="sm"
              id="filterValue"
              type="text"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder={`Enter ${currentLabel}`}
              style={inputSmStyle}
            />
          </FormGroup>

          {/* From Date */}
          <FormGroup className="mb-0" style={{ minWidth: "160px", ...font12 }}>
            <Label for="fromDate" className="mb-1 fw-bold" style={font12}>
              From Date
            </Label>
            <Input
              bsSize="sm"
              id="fromDate"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={inputSmStyle}
            />
          </FormGroup>

          {/* To Date */}
          <FormGroup className="mb-0" style={{ minWidth: "160px", ...font12 }}>
            <Label for="toDate" className="mb-1 fw-bold" style={font12}>
              To Date
            </Label>
            <Input
              bsSize="sm"
              id="toDate"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={inputSmStyle}
            />
          </FormGroup>

          {/* Buttons */}
          <div className="d-flex mb-0" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle}>
              Search
            </Button>
            <Button
              type="button"
              color="success"
              onClick={exportToExcel}
              style={buttonSmStyle}
            >
              Export To Excel
            </Button>
          </div>
        </div>
      </Form>

      {/* Table */}
      {loading ? (
        <div className="d-flex align-items-center mt-3" style={{ gap: 8 }}>
          <Spinner size="sm" /> <span>Loading Loan Card report...</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-danger">No data present!</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table
            bordered
            striped
            hover
            responsive
            size="sm"
            className="mt-3 mb-0"
          >
            <thead className="thead-light">
              <tr>
                <th style={font12}>#</th>
                <th style={font12}>Loan ID</th>
                <th style={font12}>Customer ID</th>
                <th style={font12}>EMI Number</th>
                <th style={font12}>EMI Date</th>
                <th style={font12}>EMI Amount (₹)</th>
                <th style={font12}>Principal (₹)</th>
                <th style={font12}>Interest (₹)</th>
                <th style={font12}>Outstanding Amount (₹)</th>
                <th style={font12}>Outstanding Principal (₹)</th>
                <th style={font12}>Outstanding Interest (₹)</th>
                <th style={font12}>Status</th>
                <th style={font12}>Create Date</th>
                <th style={font12}>Update Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`${r.loan_id}-${r.customer_id}-${r.emi_number ?? idx}`}>
                  <td style={font12}>{idx + 1}</td>
                  <td style={font12}>{r.loan_id}</td>
                  <td style={font12}>{r.customer_id}</td>
                  <td style={font12}>{r.emi_number}</td>
                  <td style={font12}>{r.emi_date}</td>
                  <td style={font12}>{fmt(r.emi_amount)}</td>
                  <td style={font12}>{fmt(r.principal)}</td>
                  <td style={font12}>{fmt(r.interest)}</td>
                  <td style={font12}>{fmt(r.outstanding_amount)}</td>
                  <td style={font12}>{fmt(r.outstanding_principal)}</td>
                  <td style={font12}>{fmt(r.outstanding_interest)}</td>
                  <td style={font12}>{r.status_value}</td>
                  <td style={font12}>{r.create_date}</td>
                  <td style={font12}>{r.update_date}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default LoanCardReporting;
